/**
 * The `⋯` menu beside a page header's actions, for the ones that belong to the
 * screen but not in the design's header.
 *
 * The handoff draws three buttons on the Contest Overview — Back, Edit,
 * Publish — and the product has three more transitions that have to go
 * somewhere: Cancel, Pause and Resume. Pause and Resume get their designed home
 * on the Contests list in CAE-1543; Cancel is drawn nowhere in the bundle. This
 * is where they live meanwhile, which keeps the header the shape the design
 * asks for without dropping a working action off the page.
 *
 * Built on the kit's `Popover`, which already handles the parts that are easy
 * to get wrong — moving focus into the panel and handing it back, Escape and
 * outside-click dismissal, and staying put when the page scrolls.
 */
import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { Theme } from '@mui/material/styles';
import { Popover } from '../ui';
import type { HeaderAction } from './header-action';
import { HeaderActionButton } from './header-action';

interface HeaderOverflowMenuProps {
  /** Names the menu for assistive tech, e.g. "More contest actions". */
  label: string;
  actions: readonly HeaderAction[];
  /**
   * The trigger's test id. Supplied per screen rather than fixed here — this
   * component is reachable from every page through `AdminLayout`, and a
   * contest-shaped id baked in would be the handle a second screen's tests
   * grabbed by mistake.
   */
  testId?: string;
}

export const HeaderOverflowMenu = ({
  label,
  actions,
  testId = 'header-overflow-menu-button',
}: HeaderOverflowMenuProps) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  // An empty menu renders nothing rather than a button that opens onto nothing.
  // A closed contest reaches exactly that: it can be neither cancelled, paused
  // nor resumed.
  if (actions.length === 0) return null;

  return (
    <>
      <IconButton
        ref={anchorRef}
        aria-label={label}
        aria-expanded={open}
        /*
         * `dialog`, not `menu`. The kit's `Popover` renders `role="dialog"` and
         * these are plain buttons inside it, not `menuitem`s under a `menu`
         * with arrow-key navigation. Promising menu semantics and delivering a
         * dialog is worse for a screen-reader user than naming what is actually
         * there — if this ever grows real menu roles, this changes with them.
         */
        aria-haspopup="dialog"
        data-testid={testId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        sx={(theme: Theme) => ({
          width: 36,
          height: 36,
          borderRadius: `${theme.tokens.radius.pill}px`,
          color: theme.tokens.color.ink3,
          '&:hover': {
            backgroundColor: theme.tokens.color.accentSoft,
            color: theme.tokens.color.accent,
          },
        })}
      >
        {/*
          Three dots rather than an icon from the set: the line-art kit has no
          overflow glyph, and the design never draws one. Typeset, so it inherits
          the button's colour and sits on the same baseline as the pills beside
          it.
        */}
        <Box aria-hidden component="span" sx={{ fontSize: 20, lineHeight: 1, letterSpacing: 1 }}>
          ⋯
        </Box>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        label={label}
        onClose={() => setOpen(false)}
        width={200}
      >
        <Box
          sx={(theme: Theme) => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: `${theme.tokens.space[1]}px`,
            padding: `${theme.tokens.space[1]}px`,
            // Menu items read left-aligned; the pill buttons centre their label,
            // which is right in a header row and wrong in a stacked list.
            '& .MuiButton-root': { justifyContent: 'flex-start' },
          })}
        >
          {actions.map((action) => (
            <HeaderActionButton
              key={action.label}
              action={{
                ...action,
                // Closing on the way through, so the menu does not stay open
                // over the confirmation dialog the action raises.
                onClick: action.onClick
                  ? () => {
                      setOpen(false);
                      action.onClick?.();
                    }
                  : undefined,
              }}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};
